<template>
  <div>
    <transition name="slide-from-left">
      <div
        v-show="$store.state.ui.paintCalculatorIsOpen"
        class="fixed left-0 inset-y-0 w-full max-w-lg bg-white z-80"
      >
        <div class="flex flex-col h-full">
          <div class="px-5 py-5 sm:py-6 border-b bg-white">
            <div class="grid grid-cols-6">
              <button
                class="col-span-1 link"
                @click="$store.commit('closePaintCalculator')"
              >
                <div class="w-5 h-5">
                  <IconCross />
                </div>
              </button>
              <h3 class="col-span-4 text-md sm:text-lg text-center">
                Paint Calculator
              </h3>
            </div>
          </div>
          <div
            v-show="hasError()"
            class="px-6 py-2 bg-red text-white font-semibold -mt-px"
            role="alert"
          >
            Please ensure all input is valid
          </div>
          <form
            novalidate
            class="flex-1 h-full overflow-y-auto scrolling-touch"
          >
            <div class="h-full flex flex-col">
              <div class="flex-1 px-2 sm:px-6 pb-6">
                <fieldset class="py-6">
                  <legend class="sr-only">Job Type</legend>
                  <div class="grid grid-cols-2 gap-3 sm:gap-6">
                    <div class="col-span-2 sm:col-span-1">
                      <Toggle
                        id="coating-is-new-job"
                        v-model="isNewJob"
                        :labels="['New Job', 'Re-paint']"
                      />
                    </div>
                    <div class="col-span-2 sm:col-span-1">
                      <Toggle
                        id="paint-is-interior"
                        v-model="isInterior"
                        :labels="['Interior', 'Exterior']"
                      />
                    </div>
                  </div>
                </fieldset>
                <InteriorAreaAccordion
                  v-if="isInterior"
                  :value="interiorAreas"
                  @input="(payload) => (interiorAreas = payload)"
                />
                <ExteriorAreaAccordion
                  v-else
                  :value="exteriorAreas"
                  @input="(payload) => (exteriorAreas = payload)"
                />
              </div>
              <section v-show="!hasError()">
                <div class="bg-grey py-7 px-2 sm:px-6">
                  <h3 class="text-md mb-4 flex items-center">
                    Recommended Products
                    <ButtonInfo
                      class="ml-2"
                      heading=""
                      body="All of the paints you need to get a perfectly finished project"
                    />
                  </h3>
                  <InteriorResultsPanel
                    v-if="isInterior"
                    :quantities="interiorQuantities"
                    :is-new-job="isNewJob"
                  />
                  <ExteriorResultsPanel
                    v-else
                    :quantities="exteriorQuantities"
                    :is-new-job="isNewJob"
                  />
                </div>
                <div>
                  <div
                    class="flex flex-wrap justify-between border-t py-5 px-2 sm:px-6 bg-paint"
                  >
                    <p
                      class="w-full sm:w-auto sm:text-md font-semibold mb-1 sm:mb-0 text-theme"
                    >
                      You need {{ totalQuantity | formatVolume }} total
                    </p>
                    <button
                      class="link underline text-left text-theme"
                      type="button"
                      @click="isSendFormOpen = !isSendFormOpen"
                    >
                      Email me this info
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </form>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <Modal v-show="isSendFormOpen" @close="isSendFormOpen = false">
        <PaintCalculatorEmailForm
          :interior="isInterior"
          :new-job="isNewJob"
          :quantities="isInterior ? interiorQuantities : exteriorQuantities"
          @close="handleEmailFormSubmit()"
        />
      </Modal>
    </transition>
  </div>
</template>

<script>
import * as calculator from "./lib/paintCalculator";
import config from "./config";
import Toggle from "./ui/Toggle.vue";
import ExteriorAreaAccordion from "./Accordions/ExteriorAreaAccordion.vue";
import ExteriorResultsPanel from "./ResultsPanel/ExteriorResultsPanel.vue";
import InteriorAreaAccordion from "./Accordions/InteriorAreaAccordion.vue";
import InteriorResultsPanel from "./ResultsPanel/InteriorResultsPanel.vue";
import PaintCalculatorEmailForm from "./PaintCalculatorEmailForm.vue";
import Modal from "../Modal/Modal.vue";
import ButtonInfo from "../ui/ButtonInfo.vue";
import IconCross from "../../../templates/_includes/svg/icon-cross.svg";

export default {
  name: "PaintCalculator",

  components: {
    Toggle,
    ExteriorAreaAccordion,
    ExteriorResultsPanel,
    InteriorAreaAccordion,
    InteriorResultsPanel,
    PaintCalculatorEmailForm,
    Modal,
    ButtonInfo,
    IconCross,
  },

  data() {
    return {
      isNewJob: true,
      isInterior: true,
      interiorAreas: [{ id: "0" }],
      exteriorAreas: [
        {
          ...config.areas.EXTERIOR_AREA_DEFAULTS[
            config.areas.EXTERIOR_AREA_TYPES.HOUSE
          ],
          id: "0",
        },
      ],

      isSendFormOpen: false,
    };
  },

  computed: {
    interiorQuantities() {
      const validAreas = this.interiorAreas.filter(
        (area) => area.type !== undefined
      );
      return {
        wallPaint: calculator.calculateRecommendedInteriorWallPaint(validAreas),
        primer: calculator.calculateRecommendedInteriorPrimer(validAreas),
        ceilingPaint: calculator.calculateRecommendedInteriorCeilingPaint(
          validAreas
        ),
        enamelPaint: calculator.calculateRecommendedEnamelPaint(validAreas),
      };
    },

    exteriorQuantities() {
      return {
        wallPaint: calculator.calculateRecommendedExteriorWallPaint(
          this.exteriorAreas
        ),

        primer: calculator.calculateRecommendedExteriorPrimer(
          this.exteriorAreas
        ),
      };
    },

    totalQuantity() {
      return this.isInterior
        ? calculator.calculateTotalRecommendedInteriorPaint(
            this.interiorAreas,
            this.isNewJob
          )
        : calculator.calculateTotalRecommendedExteriorPaint(
            this.exteriorAreas,
            this.isNewJob
          );
    },
  },

  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const paintCalculatorParam = urlParams.get("paint-calculator");

    if (paintCalculatorParam !== null) {
      this.$store.commit("openPaintCalculator");
    }

    const data = window.sessionStorage.getItem("paint-calculator-state");

    if (data === null) return;

    Object.entries(JSON.parse(data)).forEach(([key, value]) => {
      this[key] = value;
    });

    window.onbeforeunload = this.persistState;
  },

  beforeDestroy() {
    this.persistState();
  },

  destroyed() {
    window.onbeforeunload = undefined;
  },

  methods: {
    hasError() {
      return Number.isNaN(this.totalQuantity);
    },

    persistState() {
      window.sessionStorage.setItem(
        "paint-calculator-state",
        JSON.stringify(this.$data)
      );
    },

    handleEmailFormSubmit() {
      this.isSendFormOpen = false;
      this.$store.commit("closePaintCalculator");
    },
  },
};
</script>
